import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { map, merge, Observable, of } from 'rxjs';

import { tagLabelColor } from '../../../core/utils/tag-label-color';

@Component({
	selector: 'app-tag-chip',
	templateUrl: './app-tag-chip.component.html',
	styleUrls: ['./app-tag-chip.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTagChipComponent extends DcBaseComponent implements OnInit {
	@Input() public label = '';
	@Input() public color = 'transparent';
	@Input() public foreground: 'black' | 'white' = 'black';
	@Input() public removable = false;
	@Input() public variant: 'tag' | 'keyword' = 'tag';
	@Input() public fill: 'solid' | 'tint' = 'solid';
	/** When true, render a kebab action menu (shift-left/shift-right/remove) instead of the bare remove button. */
	@Input() public menu = false;
	@Input() public canShiftLeft = false;
	@Input() public canShiftRight = false;
	@Input() public collapsed = false;
	/**
	 * Emphasis ring on the dot (for cross-highlight from an external control). Purely
	 * visual — it does NOT reveal/expand the label the way hover/focus does, so a
	 * collapsed dot can be highlighted while staying collapsed.
	 */
	@Input() public highlighted = false;

	@Output() public readonly remove = new EventEmitter<void>();
	@Output() public readonly edit = new EventEmitter<void>();
	@Output() public readonly shiftLeft = new EventEmitter<void>();
	@Output() public readonly shiftRight = new EventEmitter<void>();

	public menuOpen = false;

	public vo$!: Observable<{
		label: string;
		color: string;
		foreground: 'black' | 'white';
		removable: boolean;
		variant: 'tag' | 'keyword';
		fill: 'solid' | 'tint';
		menu: boolean;
		canShiftLeft: boolean;
		canShiftRight: boolean;
		collapsed: boolean;
		highlighted: boolean;
		labelColor: 'black' | 'white';
	}>;

	public override ngOnInit(): void {
		super.ngOnInit();
		const color$ = merge(
			of<string>('transparent'),
			this.toObservable<string>('color')
		);
		const foreground$ = merge(
			of<'black' | 'white'>('black'),
			this.toObservable<'black' | 'white'>('foreground')
		);
		this.vo$ = combineAll({
			label: merge(of<string>(''), this.toObservable<string>('label')),
			color: color$,
			foreground: foreground$,
			labelColor: color$.pipe(map((color) => tagLabelColor(color))),
			removable: merge(
				of<boolean>(false),
				this.toObservable<boolean>('removable')
			),
			variant: merge(
				of<'tag' | 'keyword'>('tag'),
				this.toObservable<'tag' | 'keyword'>('variant')
			),
			fill: merge(
				of<'solid' | 'tint'>('solid'),
				this.toObservable<'solid' | 'tint'>('fill')
			),
			menu: merge(of<boolean>(false), this.toObservable<boolean>('menu')),
			canShiftLeft: merge(
				of<boolean>(false),
				this.toObservable<boolean>('canShiftLeft')
			),
			canShiftRight: merge(
				of<boolean>(false),
				this.toObservable<boolean>('canShiftRight')
			),
			collapsed: merge(
				of<boolean>(false),
				this.toObservable<boolean>('collapsed')
			),
			highlighted: merge(
				of<boolean>(false),
				this.toObservable<boolean>('highlighted')
			),
		});
	}

	public triggerRemove(): void {
		this.menuOpen = false;
		this.remove.emit();
	}

	public triggerEdit(): void {
		this.menuOpen = false;
		this.edit.emit();
	}

	public toggleMenu(): void {
		this.menuOpen = !this.menuOpen;
	}

	public closeMenu(): void {
		this.menuOpen = false;
	}

	public triggerShiftLeft(): void {
		this.menuOpen = false;
		this.shiftLeft.emit();
	}

	public triggerShiftRight(): void {
		this.menuOpen = false;
		this.shiftRight.emit();
	}
}
